import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { withAppHOC } from "../../hoc";
import { StyledContentContainer } from "../../components/styled";
import { MessageBox } from "../../components/FormElements";
import Loader from "../../components/Loader/Loader";
import PageHeader from "../../components/PageHeader/PageHeader";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCalculator,
  faEnvelope,
  faLocationDot,
  faPhoneAlt,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";

/* ------------------ CARD GRADIENTS ------------------ */
const cardGradients = [
  "linear-gradient(135deg, #1f1c2c, #928dab)",
  "linear-gradient(135deg, #0f2027, #2c5364)",
  "linear-gradient(135deg, #42275a, #734b6d)",
  "linear-gradient(135deg, #203a43, #0f2027)",
  "linear-gradient(135deg, #4b6cb7, #182848)",
];

/* ------------------ STYLED COMPONENTS ------------------ */
const Container = styled(StyledContentContainer)`
  padding: 32px 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 22px;
`;

const Card = styled.div`
  background: ${({ index }) => cardGradients[index % cardGradients.length]};
  padding: 22px 26px;
  border-radius: 14px;
  display: flex;
  align-items: flex-start;
  gap: 16px;

  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.12);

  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.25);
  }

  svg.icon {
    font-size: 34px;
    color: rgba(255, 255, 255, 0.85);
    flex-shrink: 0;
    margin-top: 2px;
  }

  .content {
    flex: 1;

    span.label {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.85);
      display: block;
      margin-bottom: 6px;
    }

    p.value {
      font-size: 16px;
      font-weight: 600;
      color: #ffffff;
      margin: 0;
      line-height: 1.5;
      word-break: break-word;
      display: flex;
      align-items: center;
      gap: 8px;

      .copy-icon {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.7);
        cursor: pointer;

        &:hover {
          color: #ffffff;
        }
      }
    }
  }
`;

const TermsCard = styled.div`
  background: #ffffff;
  padding: 30px 34px;
  margin-top: 32px;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  line-height: 1.7;

  h3 {
    font-size: 22px;
    font-weight: 700;
    color: #222;
    margin-bottom: 18px;
    border-bottom: 2px solid #f1f1f1;
    padding-bottom: 10px;
  }

  h4 {
    margin: 18px 0 10px;
    font-size: 16px;
    font-weight: 600;
    color: #444;
  }

  ul {
    padding-left: 22px;
    margin: 6px 0 14px;
  }

  li {
    margin-bottom: 8px;
    color: #333;
  }

  li::marker {
    color: #ff9800;
  }

  .important {
    background: #fff4e5;
    border-left: 4px solid #ffa726;
    padding: 10px 14px;
    margin: 10px 0;
    font-weight: 600;
  }

  a {
    color: #1e88e5;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #1565c0;
    }
  }
`;

/* ------------------ API FETCH ------------------ */
const fetchFirmDetails = async () => {
  const response = await API.get("/meta/firm-details");
  return response.data;
};

/* ------------------ STATIC TERMS ------------------ */
const staticSellerTerms = [
  {
    heading: "Invoice Rules",
    items: [
      "E-invoice is mandatory for clients whose sales exceed defined turnover.",
      "Upload the last month’s Sales Tax Invoice with your stamp and signature.",
      "Last date for uploading invoices: 7th of every month.",
      "Upload invoices in GSTR-1 before the 11th of every month (GST Rule 36(4)).",
      "Non-compliance may result in wallet block or suspension of further transactions.",
    ],
  },
  {
    heading: "Complaint and Pending Rules",
    items: [
      "Complaint TAT: 1 working hour; Pending TAT: 15 minutes.",
      "Strict adherence to TAT is required; violations may result in reversals or account deactivation.",
      "Refunds after complaints are held according to the grading system.",
      "If fake success increases, the company may block your wallet and halt transactions.",
      "Re-opened complaints must provide proper proof; otherwise, the recharge amount may be blocked.",
    ],
  },
  {
    heading: "Redeem Rules",
    items: [
      "Wallet redemption is allowed according to the grading system.",
      "Only accounts verified with the firm name are eligible for redemption.",
      "Payout API is used for redemption; banking issues may delay processing (48–72 working hours).",
      "Bank account changes will apply to future redemptions.",
    ],
  },
];

const staticBuyerTerms = [
  {
    heading: "General Rules",
    items: [
      "There is no registration fee at www.apiexchange.co.in",
      "Only prepaid and DTH E-top stock buy/sale is allowed.",
      "Only GST-registered persons can sell.",
      "No setup cost for Seller API integration.",
      "Live data will remain available for 60 days only.",
    ],
  },
  {
    heading: "GST & KYC Compliance",
    items: [
      "Update GST details correctly to receive valid GST invoices.",
      "Failure to update GST may result in B2C classification.",
    ],
  },
  {
    heading: "Margin & Service Rules",
    items: [
      "Minimum commission must be set on purchase margin.",
      "24/7 service is provided with unlimited E-top values.",
    ],
  },
  {
    heading: "Complaint & Pending Rules",
    items: [
      "Complaints must be registered within 5 days.",
      "Complaint registration occurs 5 minutes after transaction.",
      "Wrong-to-right transfers follow operator-specific rules.",
    ],
  },
  {
    heading: "Wallet Top-Up Rules",
    items: [
      "Auto-update requires a minimum transfer of ₹5,000.",
      "Submit correct UTR if auto-update fails.",
    ],
  },
];

/* ------------------ MAIN COMPONENT ------------------ */
const FirmDetails = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["firm_details"],
    queryFn: fetchFirmDetails,
  });

  const firm = data?.data || {};

  const renderCopyIcon = (text) => (
    <FontAwesomeIcon
      icon={faCopy}
      className="copy-icon"
      onClick={() => navigator.clipboard.writeText(text)}
      title="Copy"
    />
  );

  if (isLoading) {
    return (
      <>
        <PageHeader title="Company Profile" />
        <Container>
          <Loader isLoading />
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Company Profile" />
        <Container>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </Container>
      </>
    );
  }

  const details = [
    { icon: faBuilding, label: "Legal Name", value: firm.firmName },
    { icon: faLocationDot, label: "Head Office", value: firm.firmAddress },
    { icon: faCalculator, label: "GST Number", value: firm.gstNumber },
    { icon: faPhoneAlt, label: "Support Number", value: firm.supportNumber },
    { icon: faEnvelope, label: "Support Email", value: firm.supportEmail },
  ];

  return (
    <>
      <PageHeader title="Company Profile" />
      <Container>
        <Grid>
          {details.map((item, index) => (
            <Card key={index} index={index}>
              <FontAwesomeIcon icon={item.icon} className="icon" />
              <div className="content">
                <span className="label">{item.label}</span>
                <p className="value">
                  {item.value || "-"}
                  {item.value &&
                    (item.label === "GST Number" ||
                      item.label.includes("Email")) &&
                    renderCopyIcon(item.value)}
                </p>
              </div>
            </Card>
          ))}
        </Grid>

        <TermsCard>
          <h3>Seller Terms & Conditions</h3>
          {staticSellerTerms.map((section, i) => (
            <div key={i}>
              <h4>{section.heading}</h4>
              <ul>
                {section.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </TermsCard>

        <TermsCard>
          <h3>Buyer Terms & Conditions</h3>
          {staticBuyerTerms.map((section, i) => (
            <div key={i}>
              <h4>{section.heading}</h4>
              <ul>
                {section.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </TermsCard>
      </Container>
    </>
  );
};

export default withAppHOC(FirmDetails);